export const TOKEN_TYPES = {
  team: "Team",
  organization: "Organization",
} as const;

export type TokenType = (typeof TOKEN_TYPES)[keyof typeof TOKEN_TYPES];

export type PermissionGroup = "read" | "change";

export interface PermissionOption {
  value: string;
  label: string;
  description: string;
  organizationDescription?: string;
  group: PermissionGroup;
  tokenTypes: TokenType[];
}

export const PERMISSIONS: PermissionOption[] = [
  {
    value: "resources:read",
    label: "Resources",
    description: "Read resources the team can access",
    organizationDescription: "Read resources within the organization.",
    group: "read",
    tokenTypes: ["Team", "Organization"],
  },
  {
    value: "resources:write",
    label: "Resources",
    description: "Create resources the team can access.",
    organizationDescription: "Create resources within the organization.",
    group: "change",
    tokenTypes: ["Team", "Organization"],
  },
  {
    value: "networking:read",
    label: "Gateways and Connectors",
    description: "Read gateways and connectors within the organization",
    group: "read",
    tokenTypes: ["Team", "Organization"],
  },
  {
    value: "users:read",
    label: "Users",
    description: "Read users within the organization.",
    group: "read",
    tokenTypes: ["Organization"],
  },
  {
    value: "teams:read",
    label: "Teams",
    description: "Read teams within the organization.",
    group: "read",
    tokenTypes: ["Organization"],
  },
  {
    value: "teams:write",
    label: "Teams",
    description: "Create teams within the organization.",
    group: "change",
    tokenTypes: ["Organization"],
  },
  {
    value: "tokens:read",
    label: "Tokens",
    description: "Read team and organization tokens within the organization.",
    group: "read",
    tokenTypes: ["Organization"],
  },
];

export function permissionsFor(tokenType: TokenType) {
  return PERMISSIONS.filter((permission) =>
    permission.tokenTypes.includes(tokenType),
  );
}

export function describePermission(
  permission: PermissionOption,
  tokenType: TokenType,
): string {
  return tokenType === TOKEN_TYPES.organization
    ? (permission.organizationDescription ?? permission.description)
    : permission.description;
}

export function permissionsInGroup(
  tokenType: TokenType,
  group: PermissionGroup,
) {
  return permissionsFor(tokenType).filter(
    (permission) => permission.group === group,
  );
}

export const DEFAULT_LIFETIME_DAYS = 90;

export const LIFETIME_CHOICES = [7, 30, 90, 180, 365] as const;

export function tokenExpiresAt(
  dayEnd: string | null,
  latestExpiry: string | undefined,
): string | null {
  if (!dayEnd || !latestExpiry) {
    return dayEnd;
  }

  return new Date(dayEnd) > new Date(latestExpiry) ? latestExpiry : dayEnd;
}

export interface TokenCreator {
  userId: string;
  name: string;
  email: string;
}

export interface TokenTeam {
  teamId: string;
  name: string;
}

export interface Token {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: TokenCreator | null;
  team: TokenTeam | null;
  permissions: string[];
  lastUsedAt: string | null;
}

export type TokenState = "active" | "expired" | "revoked";

export function tokenState(token: Token, now: number = Date.now()): TokenState {
  if (token.revokedAt) {
    return "revoked";
  }

  if (token.expiresAt && new Date(token.expiresAt).getTime() <= now) {
    return "expired";
  }

  return "active";
}

export function permissionLabel(permission: string) {
  return (
    PERMISSIONS.find((entry) => entry.value === permission)?.label ?? permission
  );
}

export function tokenTypeLabel(type: string) {
  return type === TOKEN_TYPES.organization ? "Organization" : "Team";
}

export function canCreateToken({
  teams,
  teamsFailed,
  isAdmin,
}: {
  teams: unknown[] | null;
  teamsFailed: boolean;
  isAdmin: boolean;
}): boolean {
  if (teamsFailed || teams === null) {
    return false;
  }

  return teams.length > 0 || isAdmin;
}
